import React, { useCallback, useState, useRef } from 'react';
import classNames from 'classnames';
import { Input } from '../../../../components/inputs/Input';
import { BaseInputProps } from './BaseInput';
import useT from '../../../../translation/src/hooks/useT';
import { ConvertType, useCondMeterToFeet, useCondFeetToMeter } from '../../../../hooks/useFormatNumber';

export const MinMaxInput: React.FC<BaseInputProps<[number, number]>> = ({ instance, onChange, disabled = false, className }) => {
  const { config, value } = instance;
  const mixedText = useT('mixed', { swc: true });
  const minLabel = useT('min', { swc: true });
  const maxLabel = useT('max', { swc: true });
  const decimals = config.ui?.decimals;
  const convertType = config.ui?.useUserPreferenceConvertType || ConvertType.NONE;
  const { condMeterToFeet } = useCondMeterToFeet();
  const { condFeetToMeter } = useCondFeetToMeter();

  const [isMinFocused, setIsMinFocused] = useState(false);
  const [isMaxFocused, setIsMaxFocused] = useState(false);
  const [localMinValue, setLocalMinValue] = useState<string>('');
  const [localMaxValue, setLocalMaxValue] = useState<string>('');
  const minInputRef = useRef<HTMLInputElement>(null);
  const maxInputRef = useRef<HTMLInputElement>(null);

  // Get validation bounds
  const validationMin = config.validation?.min ?? 0;
  const validationMax = config.validation?.max ?? 100;

  // Update local values when external value changes, but only when not focused
  const [prevValue, setPrevValue] = useState(value);
  const [prevConvertType, setPrevConvertType] = useState(convertType);
  const [prevDecimals, setPrevDecimals] = useState(decimals);
  const valueChanged = value !== prevValue || convertType !== prevConvertType || decimals !== prevDecimals;

  if (valueChanged) {
    setPrevValue(value);
    setPrevConvertType(convertType);
    setPrevDecimals(decimals);
    if (!isMinFocused && Array.isArray(value)) {
      const convertedMin = condMeterToFeet(value[0], convertType);
      const formattedMin = decimals !== undefined ? convertedMin.toFixed(decimals) : convertedMin.toString();
      setLocalMinValue(formattedMin);
    }
    if (!isMaxFocused && Array.isArray(value)) {
      const convertedMax = condMeterToFeet(value[1], convertType);
      const formattedMax = decimals !== undefined ? convertedMax.toFixed(decimals) : convertedMax.toString();
      setLocalMaxValue(formattedMax);
    }
  }

  const applyConstraints = useCallback(
    (minVal: number, maxVal: number): [number, number] => {
      let constrainedMin = minVal;
      let constrainedMax = maxVal;

      // Apply validation bounds
      if (constrainedMin < validationMin) constrainedMin = validationMin;
      if (constrainedMin > validationMax) constrainedMin = validationMax;
      if (constrainedMax < validationMin) constrainedMax = validationMin;
      if (constrainedMax > validationMax) constrainedMax = validationMax;

      // Ensure min <= max
      if (constrainedMin > constrainedMax) {
        constrainedMin = constrainedMax;
      }

      return [constrainedMin, constrainedMax];
    },
    [validationMin, validationMax],
  );

  const handleMinChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      setLocalMinValue(inputValue);

      const newMin = parseFloat(inputValue);
      if (!isNaN(newMin)) {
        // Convert user input to meters (condFeetToMeter handles the conversion based on user preference)
        const newMinInMeters = condFeetToMeter(newMin, convertType);
        const currentMax = Array.isArray(value) ? value[1] : validationMax;
        const [constrainedMin, constrainedMax] = applyConstraints(newMinInMeters, currentMax);
        onChange([constrainedMin, constrainedMax]);
      }
    },
    [value, validationMax, onChange, applyConstraints, convertType, condFeetToMeter],
  );

  const handleMaxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      setLocalMaxValue(inputValue);

      const newMax = parseFloat(inputValue);
      if (!isNaN(newMax)) {
        // Convert user input to meters (condFeetToMeter handles the conversion based on user preference)
        const newMaxInMeters = condFeetToMeter(newMax, convertType);
        const currentMin = Array.isArray(value) ? value[0] : validationMin;
        const [constrainedMin, constrainedMax] = applyConstraints(currentMin, newMaxInMeters);
        onChange([constrainedMin, constrainedMax]);
      }
    },
    [value, validationMin, onChange, applyConstraints, convertType, condFeetToMeter],
  );

  const handleMinFocus = () => {
    setIsMinFocused(true);
  };

  const handleMinBlur = () => {
    setIsMinFocused(false);
    commitMinValue();
  };

  const handleMaxFocus = () => {
    setIsMaxFocused(true);
  };

  const handleMaxBlur = () => {
    setIsMaxFocused(false);
    commitMaxValue();
  };

  const commitMinValue = () => {
    const numValue = parseFloat(localMinValue);
    if (!isNaN(numValue)) {
      // Convert user input to meters
      const numValueInMeters = condFeetToMeter(numValue, convertType);
      const currentMax = Array.isArray(value) ? value[1] : validationMax;
      const [constrainedMin, constrainedMax] = applyConstraints(numValueInMeters, currentMax);
      onChange([constrainedMin, constrainedMax]);

      // Update local value to show the constrained result (convert meters back to user preference)
      const constrainedMinDisplay = condMeterToFeet(constrainedMin, convertType);
      const formattedValue = decimals !== undefined ? constrainedMinDisplay.toFixed(decimals) : constrainedMinDisplay.toString();
      setLocalMinValue(formattedValue);
    } else {
      // Invalid input - revert to current value (convert meters to user preference)
      const currentMin = Array.isArray(value) ? value[0] : validationMin;
      const currentMinDisplay = condMeterToFeet(currentMin, convertType);
      const formattedValue = decimals !== undefined ? currentMinDisplay.toFixed(decimals) : currentMinDisplay.toString();
      setLocalMinValue(formattedValue);
    }
  };

  const commitMaxValue = () => {
    const numValue = parseFloat(localMaxValue);
    if (!isNaN(numValue)) {
      // Convert user input to meters
      const numValueInMeters = condFeetToMeter(numValue, convertType);
      const currentMin = Array.isArray(value) ? value[0] : validationMin;
      const [constrainedMin, constrainedMax] = applyConstraints(currentMin, numValueInMeters);
      onChange([constrainedMin, constrainedMax]);

      // Update local value to show the constrained result (convert meters back to user preference)
      const constrainedMaxDisplay = condMeterToFeet(constrainedMax, convertType);
      const formattedValue = decimals !== undefined ? constrainedMaxDisplay.toFixed(decimals) : constrainedMaxDisplay.toString();
      setLocalMaxValue(formattedValue);
    } else {
      // Invalid input - revert to current value (convert meters to user preference)
      const currentMax = Array.isArray(value) ? value[1] : validationMax;
      const currentMaxDisplay = condMeterToFeet(currentMax, convertType);
      const formattedValue = decimals !== undefined ? currentMaxDisplay.toFixed(decimals) : currentMaxDisplay.toString();
      setLocalMaxValue(formattedValue);
    }
  };

  const handleMinKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitMinValue();
      minInputRef.current?.blur();
    }
  };

  const handleMaxKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitMaxValue();
      maxInputRef.current?.blur();
    }
  };

  // min, max are the absolute bounds from validation, minVal, maxVal are the current values
  const [min, max] = [
    config.validation?.min ? (decimals !== undefined ? config.validation.min.toFixed(decimals) : config.validation.min.toString()) : '0',
    config.validation?.max ? (decimals !== undefined ? config.validation.max.toFixed(decimals) : config.validation.max.toString()) : '100',
  ];

  const minDisplayValue = isMinFocused
    ? localMinValue
    : Array.isArray(value)
      ? (() => {
          const convertedMin = condMeterToFeet(value[0], convertType);
          return decimals !== undefined ? convertedMin.toFixed(decimals) : convertedMin.toString();
        })()
      : min;
  const maxDisplayValue = isMaxFocused
    ? localMaxValue
    : Array.isArray(value)
      ? (() => {
          const convertedMax = condMeterToFeet(value[1], convertType);
          return decimals !== undefined ? convertedMax.toFixed(decimals) : convertedMax.toString();
        })()
      : max;

  return (
    <div className={classNames('flex space-x-2 items-center flex-1', className)}>
      <div className="flex-1">
        <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">{minLabel}</label>
        <Input
          ref={minInputRef}
          type="number"
          value={instance.isMixed && !isMinFocused ? '' : minDisplayValue}
          onChange={handleMinChange}
          onFocus={handleMinFocus}
          onBlur={handleMinBlur}
          onKeyDown={handleMinKeyDown}
          disabled={disabled || config.disabled}
          className={classNames('w-full', instance.isMixed && !isMinFocused && 'text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800 italic')}
          step={config.validation?.step}
          placeholder={instance.isMixed ? mixedText : ''}
        />
      </div>
      <div className="flex-1">
        <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">{maxLabel}</label>
        <Input
          ref={maxInputRef}
          type="number"
          value={instance.isMixed && !isMaxFocused ? '' : maxDisplayValue}
          onChange={handleMaxChange}
          onFocus={handleMaxFocus}
          onBlur={handleMaxBlur}
          onKeyDown={handleMaxKeyDown}
          disabled={disabled || config.disabled}
          className={classNames('w-full', instance.isMixed && !isMaxFocused && 'text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800 italic')}
          step={config.validation?.step}
          placeholder={instance.isMixed ? mixedText : ''}
        />
      </div>
    </div>
  );
};
